import * as XLSX from 'xlsx';
import { import_data_from_excel } from '../api/excel_api';
import { isValidIsraeliID, isOnlyNumbers, isValidPhoneNumber, isValidEmail, isValidPassport } from '../utils/validationId';
import { parseExcelDate } from '../utils/dateUtils';

const buildRejectedRowsReport = (rows) => rows.map((row) => {
    const idLabel = `מזהה: ${row.id}`;
    return `שורה ${row.rowNumber} (${row.name}, ${idLabel}):\n  ${row.errors.join('\n  ')}`;
}).join('\n\n');

const downloadRejectedRowsReport = (rows, title) => {
    if (!rows.length) {
        return;
    }

    const content = `${title}\n\n${buildRejectedRowsReport(rows)}`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `import-errors-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const normalizeId = (rawId) => {
    const cleanId = String(rawId || '').trim();
    if (!/^\d+$/.test(cleanId) || cleanId.length >= 9) {
        return cleanId;
    }

    const paddedId = cleanId.padStart(9, '0');
    return isValidIsraeliID(paddedId) ? paddedId : cleanId;
};

const parseMeetingDatesCell = (value) => {
    if (value == null || value === '') {
        return [];
    }

    if (value instanceof Date || typeof value === 'number') {
        const singleDate = parseExcelDate(value, { fallbackToToday: false });
        return singleDate ? [singleDate] : [];
    }

    if (Array.isArray(value)) {
        return value.map((item) => parseExcelDate(item, { fallbackToToday: false })).filter(Boolean);
    }

    return value
        .toString()
        .split(',')
        .map((item) => parseExcelDate(item.trim(), { fallbackToToday: false }))
        .filter(Boolean);
};

export const importFromExcel = async (file) => {
    try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);

        if (rows.length === 0) {
            alert('הקובץ ריק או לא בפורמט נתמך');
            return;
        }

        const parsedData = [];
        const skippedRows = [];

        rows.forEach((row, index) => {
            const rowNumber = index + 2; // Excel row number (1 is header)

            // Extract required fields
            const id = normalizeId(row['ת.ז.']);
            const first_name = row['שם פרטי']?.toString().trim() || '';
            const last_name = row['שם משפחה']?.toString().trim() || '';

            // Extract action fields
            const payment_to_supplier = row['תשלום לספק']?.toString().trim() || '';
            const payment_method = row['תשלום לנתמך']?.toString().trim() || '';
            const category = row['סעיף']?.toString().trim() || '';
            const welfare_subcategory = row['סוג סיוע ורווחה']?.toString().trim() || '';
            const meeting_count = row['מספר מפגשים'];
            const meeting_dates = parseMeetingDatesCell(row['תאריכי מפגשים'] ?? row['תאריך מפגש']);
            const amount = row['סכום'];
            const check_date = row['תאריך שיק'];
            const check_number = row['מספר שיק']?.toString().trim() || '';
            const check_amount = row['סכום שיק'];

            // Validation errors for this row
            const errors = [];

            // Define allowed values
            const allowedSupplierPaymentMethods = ['העברה בנקאית לספק'];
            const allowedBeneficiaryPaymentMethods = ['שובר קנייה', 'העברה בנקאית ישירה', 'שיק', 'תמיכה שוות כסף'];
            const allowedCategories = ['סיוע ורווחה', 'הכנסת כלה', 'שכ"ל', 'כולל', 'אחר'];
            const allowedWelfareSubcategories = ['טיפול ע"י מטפל', 'סיוע כספי פרטי'];
            const allowedCases = ['קל', 'קשה'];

            // 1. Validate mandatory fields (Supported Entity)
            if (!id) {
                errors.push('תעודת זהות או דרכון חסר');
            } else {
                const isValidID = isOnlyNumbers(id) && isValidIsraeliID(id);
                const isValidPass = isValidPassport(id);

                if (!isValidID && !isValidPass) {
                    errors.push(`תעודת זהות או דרכון לא תקין: ${id}`);
                }
            }

            if (!first_name) {
                errors.push('שם פרטי חסר');
            }

            if (!last_name) {
                errors.push('שם משפחה חסר');
            }

            // 2. Validate conditional action fields
            const hasPaymentMethod = Boolean(payment_method || payment_to_supplier);
            const hasCategory = Boolean(category);
            const hasAmount = Boolean(amount && amount !== '' && !isNaN(parseFloat(amount)));

            const actionFieldsCount = [hasPaymentMethod, hasCategory, hasAmount].filter(Boolean).length;

            if (actionFieldsCount > 0 && actionFieldsCount < 3) {
                // Partial action data is not allowed
                if (!hasPaymentMethod) errors.push('תשלום לנתמך או תשלום לספק חסר - חובה כאשר יש פעולה');
                if (!hasCategory) errors.push('סעיף חסר - חובה כאשר יש פעולה');
                if (!hasAmount) errors.push('סכום חסר - חובה כאשר יש פעולה');
            }

            // Validate supplier payment method value if provided
            if (payment_to_supplier && !allowedSupplierPaymentMethods.includes(payment_to_supplier)) {
                errors.push(`תשלום לספק לא תקין: "${payment_to_supplier}". יש לבחור אחד מהערכים: ${allowedSupplierPaymentMethods.join(', ')}`);
            }

            // Validate beneficiary payment method value if provided
            if (payment_method && !allowedBeneficiaryPaymentMethods.includes(payment_method)) {
                errors.push(`תשלום לנתמך לא תקין: "${payment_method}". יש לבחור אחד מהערכים: ${allowedBeneficiaryPaymentMethods.join(', ')}`);
            }
            if (payment_method === 'שיק') {
                if (!check_date) {
                    errors.push('תאריך שיק חובה כאשר תשלום לנתמך הוא שיק');
                }

                if (!check_number) {
                    errors.push('מספר שיק חובה כאשר תשלום לנתמך הוא שיק');
                }

                if (!check_amount) {
                    errors.push('סכום שיק חובה כאשר תשלום לנתמך הוא שיק');
                }
            }
            // Validate category value if provided
            if (hasCategory && !allowedCategories.includes(category)) {
                errors.push(`סעיף לא תקין: "${category}". יש לבחור אחד מהערכים: ${allowedCategories.join(', ')}`);
            }

            if (category === 'סיוע ורווחה') {
                if (!welfare_subcategory || !allowedWelfareSubcategories.includes(welfare_subcategory)) {
                    errors.push(`סוג סיוע ורווחה לא תקין: "${welfare_subcategory || ''}". יש לבחור אחד מהערכים: ${allowedWelfareSubcategories.join(', ')}`);
                }

                if (welfare_subcategory === 'טיפול ע"י מטפל') {
                    if (!meeting_count || isNaN(parseInt(meeting_count, 10)) || parseInt(meeting_count, 10) <= 0) {
                        errors.push('מספר מפגשים חובה וחייב להיות מספר חיובי כאשר סוג הסיוע הוא טיפול ע"י מטפל');
                    }
                    if (meeting_dates.length === 0) {
                        errors.push('לפחות תאריך מפגש אחד חובה כאשר סוג הסיוע הוא טיפול ע"י מטפל');
                    } else if (parseInt(meeting_count, 10) !== meeting_dates.length) {
                        errors.push('מספר המפגשים חייב להתאים לכמות תאריכי המפגשים כאשר סוג הסיוע הוא טיפול ע"י מטפל');
                    }
                }
            }

            // Validate מקרה (case) value if provided
            const type_case = row['דירוג מקרה']?.toString().trim() || '';
            if (type_case && !allowedCases.includes(type_case)) {
                errors.push(`דירוג מקרה לא תקין: "${type_case}". יש לבחור אחד מהערכים: ${allowedCases.join(', ')}`);
            }

            // 3. Validate amount if provided
            if (hasAmount && (isNaN(parseFloat(amount)) || parseFloat(amount) <= 0)) {
                errors.push(`סכום לא תקין: ${amount}`);
            }

            // 4. Validate optional fields
            const email = row['אימייל']?.toString().trim() || '';
            const phone = row['פלאפון']?.toString().trim() || '';
            const teacher_phone = row["פלא' מורה מקשרת"]?.toString().trim() || '';

            if (email && !isValidEmail(email)) {
                errors.push(`אימייל לא תקין: ${email}`);
            }

            if (phone && !isValidPhoneNumber(phone)) {
                errors.push(`מספר פלאפון לא תקין: ${phone}`);
            }

            if (teacher_phone && !isValidPhoneNumber(teacher_phone)) {
                errors.push(`מספר פלאפון מורה מקשרת לא תקין: ${teacher_phone}`);
            }

            // If there are validation errors, skip this row
            if (errors.length > 0) {
                skippedRows.push({
                    rowNumber,
                    name: `${first_name} ${last_name}`.trim() || 'לא צוין',
                    id: id || 'לא צוין',
                    errors
                });
                return;
            }

            // Parse the row data for backend
            parsedData.push({
                rowNumber,
                first_name,
                last_name,
                id,
                address: row['כתובת']?.toString().trim() || null,
                email,
                phone,
                referring_teacher: row['מורה מקשרת']?.toString().trim() || null,
                teacher_phone,
                type_case: row['דירוג מקרה']?.toString().trim() || null,
                comments: row['הערות']?.toString().trim() || null,

                // Action fields (only if all three are provided)
                payment_to_supplier: hasPaymentMethod && hasCategory && hasAmount ? payment_to_supplier : '',
                payment_method: hasPaymentMethod && hasCategory && hasAmount ? payment_method : '',
                provider_name: hasPaymentMethod && hasCategory && hasAmount ? (row['שם ספק']?.toString().trim() || '') : '',
                category: hasPaymentMethod && hasCategory && hasAmount ? category : '',
                welfare_subcategory: hasPaymentMethod && hasCategory && hasAmount ? welfare_subcategory : '',
                meeting_count: hasPaymentMethod && hasCategory && hasAmount && welfare_subcategory === 'טיפול ע"י מטפל'
                    ? parseInt(meeting_count, 10)
                    : null,
                meeting_dates: hasPaymentMethod && hasCategory && hasAmount && welfare_subcategory === 'טיפול ע"י מטפל'
                    ? meeting_dates
                    : null,
                amount: hasPaymentMethod && hasCategory && hasAmount ? parseFloat(amount) : null,
                date: hasPaymentMethod && hasCategory && hasAmount ?
                    (row['תאריך פעולה'] && row['תאריך פעולה'] !== 0
                        ? parseExcelDate(row['תאריך פעולה'], { fallbackToToday: true })
                        : parseExcelDate(null, { fallbackToToday: true })
                    )
                    : null,
                transaction_comments: hasPaymentMethod && hasCategory && hasAmount ? (row['הערות לפעולה']?.toString().trim() || '') : ''
                ,
                check_date: payment_method === 'שיק'
                    ? parseExcelDate(check_date, { fallbackToToday: false })
                    : null,

                check_number: payment_method === 'שיק'
                    ? check_number
                    : null,

                check_amount: payment_method === 'שיק'
                    ? parseFloat(check_amount)
                    : null
            });
        });

        // Show frontend validation errors if any
        if (parsedData.length === 0 && skippedRows.length > 0) {
            downloadRejectedRowsReport(skippedRows, 'דוח שורות שנדחו בייבוא אקסל');
            alert(`ייבוא נכשל - כל השורות נדחו.\n\nהורד דוח מפורט עם כל ${skippedRows.length} השורות שנדחו.`);
            return;
        }

        // Send valid data to backend
        const response = await import_data_from_excel(parsedData);

        // Combine frontend and backend skipped rows
        const allSkippedRows = [...skippedRows];
        if (response.skipped && response.skipped.length > 0) {
            allSkippedRows.push(...response.skipped.map(item => ({
                rowNumber: item.rowNumber || '?',
                name: item.name || 'לא צוין',
                id: item.id || 'לא צוין',
                errors: item.errors || [item.reason || 'שגיאה לא ידועה']
            })));
        }

        // Show results
        const created = response.inserted?.length || response.summary?.inserted || 0;
        const updated = response.updated?.length || response.summary?.updated || 0;

        const duplicates = response.duplicates?.length || response.summary?.duplicates || 0;
        const total = parsedData.length;

        if (allSkippedRows.length > 0) {
            const successMsg = created + updated > 0 ?
                `✓ הועלו ${created + updated} שורות בהצלחה (${created} חדשים, ${updated} עודכנו)\n` : '';

            const duplicateMsg = duplicates > 0 ?
                `⚠ ${duplicates} פעולות כפולות לא עודכנו\n` : '';

            const previewRows = allSkippedRows.slice(0, 5);
            const previewMessages = buildRejectedRowsReport(previewRows);
            const moreErrors = allSkippedRows.length > previewRows.length
                ? `\n\nהורד דוח מפורט עם כל ${allSkippedRows.length} השורות שנדחו.`
                : '';

            downloadRejectedRowsReport(allSkippedRows, 'דוח שורות שנדחו בייבוא אקסל');
            alert(`ייבוא ${created + updated > 0 ? 'חלקי' : 'נכשל'}:\n${successMsg}${duplicateMsg}✗ נדחו ${allSkippedRows.length} שורות מתוך ${total}\n\n${previewMessages}${moreErrors}`);
        } else {
            const duplicateMsg = duplicates > 0 ?
                `\n⚠ ${duplicates} פעולות כפולות לא עודכנו` : '';
            alert(`✓ ייבוא הושלם בהצלחה!\n${created} נתמכים חדשים נוצרו\n${updated} נתמכים עודכנו${duplicateMsg}`);
        }

    } catch (error) {
        console.error('Import error:', error);
        alert(error.message || 'שגיאה בהעלאת הנתונים');
    }
};
